import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Trophy } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment..." });
  const attempts = useRef(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session id provided." });
      return;
    }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "success", message: "Subscription activated!", plan: data.plan_id });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "Payment session expired. Please try again." });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "error", message: "Took too long. Check your email or refresh in a minute." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", message: "Couldn't verify payment. Please contact support." });
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-[#27272A] bg-[#121212] p-10 rounded-sm text-center" data-testid="billing-status-card">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-16 h-16 text-[#178AE5] mx-auto mb-6 animate-spin" />
            <div className="font-display text-3xl mb-2">CONFIRMING...</div>
            <p className="text-[#A1A1AA]">{state.message}</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#34C759]/10 border border-[#34C759] rounded-full mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#34C759]" />
            </div>
            <div className="font-display text-4xl text-[#34C759] mb-2">YOU'RE IN!</div>
            <p className="text-[#A1A1AA] mb-8">Your subscription is now active. Time to dominate.</p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-[#E13C32] hover:bg-[#EB5A50] text-white font-label h-12 rounded-sm"
              data-testid="billing-success-continue"
            >
              <Trophy className="w-4 h-4 mr-2" /> Back to my arena
            </Button>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#E13C32]/10 border border-[#E13C32] rounded-full mb-6">
              <XCircle className="w-10 h-10 text-[#E13C32]" />
            </div>
            <div className="font-display text-3xl mb-2">SOMETHING WENT WRONG</div>
            <p className="text-[#A1A1AA] mb-8">{state.message}</p>
            <Link to="/dashboard/billing" data-testid="billing-error-retry">
              <Button className="w-full bg-[#178AE5] hover:bg-[#41A0E8] text-white font-label h-12 rounded-sm">
                Back to billing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
